import { useEffect, useCallback, useMemo } from "react";
import { airportSearchString, updateAirportOptions } from "src/utils/airportHelper";
import { debounce } from "lodash";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
import { formatFunction, parseFunction } from "./helpers";

// Store / Retrieve airport info from redux.
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAirportOption } from "src/redux/reducers/StructuresReducer";

interface IAirportSelectFieldProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
}

/**
 * Sepcialized wrapper for "built-in" oc airport select field.
 * @param props -
 */
export const AirportSelectField = (props: IAirportSelectFieldProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  const dispatch = useDispatch();
  const structures = useSelector((state: IRootState) => state.structures);
  const { airportOptions } = structures;
  const airports = useMemo(() => Object.values(airportOptions), [airportOptions]);

  // Load default values for airport data so they display in the selection menus.
  const loadAirportData = useCallback(
    (icao: string) => {
      updateAirportOptions(icao, airportOptions, dispatch);
    },
    [airportOptions, dispatch]
  );

  // If a default value is passed in, retrieve that airport.
  useEffect(() => {
    if (defaultValue) loadAirportData(defaultValue as string);
  }, [defaultValue, loadAirportData]);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, airports, valueKey, isMulti),
    [airports, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Airport Selector"}
      onInputChange={debounce(loadAirportData, 500)}
      isMulti={isMulti}
      options={airports}
      // Add Airline ICAO (Ident) and IATA to option label so that either can be used for search
      getOptionLabel={(airport: IAirportOption) =>
        `${airport.ident} - ${airport.iata ? `${airport.iata} - ` : ""}${airport.name}`
      }
      // Display only ICAO and Name in the label.
      formatOptionLabel={(airport: any) => `${airport.ident} - ${airport.name}`}
      getOptionValue={(airport: IAirportOption) => airport._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder="Search Airport ICAO/Name"
      noOptionsMessage={() => airportSearchString}
      defaultValue={defaultValue}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
